import { useState } from "react";
import type { GreenfieldClientHubProps } from "../types.js";

export function GreenfieldReviewView({
  shell,
  preview,
  active,
  isExternalProof,
  changeMessage,
  canAct,
  onApprove,
  onRequestChange,
}: Pick<
  GreenfieldClientHubProps,
  "shell" | "preview" | "isExternalProof" | "changeMessage" | "onApprove" | "onRequestChange"
> & {
  readonly active: boolean;
  readonly canAct: boolean;
}) {
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);
  const approveLabel = isExternalProof ? "Approve external proof" : "Approve this exact preview";
  const headline = shell.headline || "Your website preview is ready.";
  const canRequestChange = canAct && changeMessage.trim().length > 0;
  const canApprove = canAct && (isExternalProof || consentAcknowledged);

  if (!active) return null;

  return (
    <section className="ws-card" id="gf-tab-review" role="tabpanel" aria-labelledby="gf-tab-btn-review">
      <h1 className="ws-display">{headline}</h1>
      <p className="ws-muted">
        {shell.subcopy || "Review the exact preview, then approve it or send one clear change request."}
      </p>
      {!canAct ? (
        <p role="status">Decision recorded. Your agency continues with the next proof-backed step.</p>
      ) : (
        <>
          {!isExternalProof && preview?.previewVersionId ? (
            <p className="ws-muted" data-testid="approval-version-ref">
              You are approving exact preview version {preview.previewVersionId}
            </p>
          ) : null}
          {!isExternalProof ? (
            <label className="ws-field" data-testid="approval-consent">
              <input
                type="checkbox"
                checked={consentAcknowledged}
                onChange={(event) => setConsentAcknowledged(event.target.checked)}
              />
              I confirm this is the exact preview shown and I consent to approval on this reference.
            </label>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="ws-btn ws-btn-primary" disabled={!canApprove} onClick={onApprove}>
              {approveLabel}
            </button>
            <button
              type="button"
              className="ws-btn"
              disabled={!canRequestChange}
              onClick={onRequestChange}
            >
              Request changes
            </button>
          </div>
        </>
      )}
    </section>
  );
}
